import { useRouter } from "next/router";
import { getPageData, getGlobalData } from "utils/api";
import { getLocalizedPaths } from "utils/localize";

import Custom404 from "./404";

import Seo from "@/components/elements/seo";
import SeoJSON from "@/components/elements/seoJSON";
import Layout from "@/components/layout";
import Sections from "@/components/sections";

// The file is called [[...slug]].js because we're using Next's
// optional catch all routes feature. See the related docs:
// https://nextjs.org/docs/routing/dynamic-routes#optional-catch-all-routes

const DynamicPage = ({
  sections,
  metadata,
  preview,
  global,
  pageContext,
  versionsAvailable,
  version,
}) => {
  const router = useRouter();

  // Check if the required data was provided
  if (!router.isFallback && !sections?.length) {
    return <Custom404 global={global} />;
  }

  // Merge default site SEO settings with page specific SEO settings
  if (metadata?.metaImage?.data === null) {
    delete metadata?.metaImage;
  }
  const metadataWithDefaults = {
    ...global.attributes.metadata,
    ...metadata,
  };

  // Loading screen (only possible in preview mode)
  if (router.isFallback) {
    return <div className="container">Loading...</div>;
  }

  return (
    <Layout global={global} pageContext={pageContext}>
      {/* Add meta tags for SEO*/}
      <Seo metadata={metadataWithDefaults} />
      <SeoJSON structuredData={metadata?.structuredData} />
      {/* Display content sections */}
      <Sections
        version={version}
        versionsAvailable={versionsAvailable}
        sections={sections}
        preview={preview}
      />
    </Layout>
  );
};

export async function getServerSideProps(context) {
  const {
    params,
    locale,
    locales,
    defaultLocale,
    preview = null,
    query,
  } = context;
  const globalLocale = await getGlobalData(locale);
  const slug = (!params?.slug ? [""] : params.slug).join("/");
  const pageData = await getPageData({
    slug,
    locale,
    preview: preview || false,
  });

  // Handle missing/empty response (undefined, null, or empty array)
  if (!pageData || (Array.isArray(pageData) && pageData.length === 0)) {
    return {
      props: {
        // Normalize global to a single consistent shape
        global: globalLocale?.data ?? globalLocale,
      },
    };
  }

  // Preview mode may return an array of versions; non-preview usually returns a single node
  const versionsAvailable =
    preview && Array.isArray(pageData)
      ? pageData.map(v => v?.attributes?.versionNumber).filter(Boolean)
      : null;

  const version =
    preview && versionsAvailable?.length
      ? !query?.version
        ? versionsAvailable[versionsAvailable.length - 1]
        : +query.version < 1
          ? versionsAvailable[versionsAvailable.length - 1]
          : +query.version
      : null;

  const filterData =
    preview && Array.isArray(pageData) && version
      ? pageData.filter(d => d?.attributes?.versionNumber === version)
      : null;

  if (preview && Array.isArray(filterData) && filterData.length < 1) {
    return {
      props: {
        global: globalLocale?.data ?? globalLocale,
      },
    };
  }

  // Support both shapes: { attributes: ... } and { data: { attributes: ... } }
  const baseAttributes = preview
    ? filterData[0]?.attributes
    : (pageData?.attributes ?? pageData?.data?.attributes);

  // If attributes are still missing, bail out gracefully
  if (!baseAttributes) {
    return {
      props: {
        global: globalLocale?.data ?? globalLocale,
      },
    };
  }

  const {
    contentSections,
    seo,
    localizations,
    slug: pageSlug,
    protectedPage,
  } = baseAttributes;

  const pageContext = {
    locale,
    locales,
    defaultLocale,
    slug: pageSlug ?? slug,
    localizations,
    protectedPage,
  };

  const localizedPaths = getLocalizedPaths(pageContext);

  // const contentSectionsMD = await Promise.all(contentSections.map(async section => {
  //   console.log(section.__typename)
  //   if(section.__typename === 'ComponentSectionsDoubleCardBlock'){

  //     const cards = await Promise.all(section.cards.map(async (card) => {
  //       if(card.smallText){
  //         const md = await convertMDX(card.smallText);
  //         console.log(JSON.stringify(md))
  //         return {
  //           ...card,
  //           smallText: md
  //         }
  //       }
  //     }))

  //     return {
  //       ...section,
  //       cards
  //     }
  //   }

  //   return section;
  // }));

  return {
    props: {
      preview,
      versionsAvailable,
      version,
      sections: contentSections ?? [],
      metadata: seo ?? null,
      // Keep `global` shape consistent with other returns
      global: globalLocale?.data ?? globalLocale,
      pageContext: {
        ...pageContext,
        localizedPaths,
      },
    },
  };
}

export default DynamicPage;
